/**
 * Interceptor
 * This is ErrorHandlerInterceptor that adds a default error handler to all requests.
 */


import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND } from 'http-status-codes';
import { LoggerService } from '@core/services/logger.service';
import { environment } from '@environment/environment';
import { LocalStorageService } from '@core/services/local-storage.service';
import { SubscriptionStateService } from '@app/shared/services/subscription-state.service';

export const SUBSCRIPTION_EXPIRED_REASON = 'SUBSCRIPTION_EXPIRED';

const PROFILE_ROUTES: { [userType: string]: string } = {
  '2': '/shipper/profile',
  '3': '/carrier/profile',
  '5': '/owner-operator/profile'
};

/**
 * Creates a ErrorHandlerInterceptor.
 * @class ErrorHandlerInterceptor
 * @description
 *  This class is used to add error handler for all api requests
 */

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  constructor(
    private logger: LoggerService,
    private router: Router,
    private localStorage: LocalStorageService,
    private subscriptionState: SubscriptionStateService
  ) { }
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(catchError(error => this.errorHandler(error)));
  }

  /**
  * Customize the default error handler here if needed
  * @param {response} HttpEvent
  * @returns Http Response Object
  */
  // Customize the default error handler here if needed
  private errorHandler(response: HttpEvent<any>): Observable<HttpEvent<any>> {
    if (!environment.production) {
      // Do something with the error
      this.logger.logError('Request error ' + JSON.stringify(response));
    }

    // console.error(error);
    const httpErrorCode = response['status'];
    switch (httpErrorCode) {
      case UNAUTHORIZED:
        this.router.navigateByUrl('/users/login');
        break;
      case FORBIDDEN:
        if (response['error']?.reason === SUBSCRIPTION_EXPIRED_REASON) {
          this.handleSubscriptionExpired();
        } else {
          this.router.navigateByUrl('/pages/403');
        }
        break;
      case NOT_FOUND:
        this.router.navigateByUrl('/pages/404');
        break;
      default:

    }

    throw response;
  }

  /**
   * The backend rejected a request because the subscription expired (e.g. it lapsed mid-session):
   * apply the expired restriction locally and send the user to My Profile to renew.
   */
  private handleSubscriptionExpired(): void {
    this.subscriptionState.update({ status: 'expired', expiryDate: null, planId: null, canRenew: true });
    const profileRoute = PROFILE_ROUTES[this.localStorage.getValueByKey('userType')];
    if (!profileRoute) {
      this.router.navigateByUrl('/pages/403');
      return;
    }
    // Already on My Profile: stay put so a rejected profile-page call cannot cause a redirect loop.
    if (this.router.url.split('?')[0] !== profileRoute) {
      this.router.navigateByUrl(profileRoute);
    }
  }

}

